/**
 * NotificationBell Component
 * 
 * Bell icon with unread badge and dropdown panel for platform notifications
 * Works for both admin and client users
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  BellIcon, 
  CheckIcon, 
  Cog6ToothIcon,
  MegaphoneIcon,
  ExclamationTriangleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { 
  getNotificationsForUser,
  getUserNotificationPreferences,
  markAllAsRead,
  dismissNotification
} from '../../services/notifications/platformNotifications.service';
import { NotificationDisplayItem, NotificationType } from '../../types/notification.types';
import { useLocation, useNavigate } from 'react-router-dom';
import { logger } from '../../utils/logger';

const UTILITY_SCALE = {
  rest: { scale: 1 },
  hover: { scale: 1.04 },
};

const BELL_RING = {
  rest: { rotate: 0 },
  hover: { rotate: [-12, 12, -8, 8, 0] },
};

const ICON_MOTION_TRANSITION = { duration: 0.15 };
const BELL_RING_TRANSITION = { duration: 0.45, ease: 'easeInOut' as const };

// Type icon mapping
const typeIcons: Record<NotificationType, React.ComponentType<{ className?: string }>> = {
  announcement: MegaphoneIcon,
  system_update: ExclamationTriangleIcon,
  feature: SparklesIcon
};

// Format relative time
const formatRelativeTime = (timestamp: any): string => {
  if (!timestamp) return 'Just now';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

interface NotificationBellProps {
  userId: string;
  userRole: 'admin' | 'client';
  userCreatedAt?: Date;
  onOpenChange?: (open: boolean) => void;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
  userId,
  userRole,
  userCreatedAt,
  onOpenChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<NotificationDisplayItem[]>([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const iconHover = reduceMotion ? undefined : 'hover';

  const setOpen = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      onOpenChange?.(open);
    },
    [onOpenChange]
  );

  // Fetch notifications on mount and periodically
  const fetchNotifications = useCallback(async () => {
    // Don't fetch if no userId (user not authenticated yet)
    if (!userId) {
      return;
    }
    
    try {
      // Get user preferences first
      const prefs = await getUserNotificationPreferences(userId);
      
      // Get notifications filtered by role and preferences
      const notifs = await getNotificationsForUser(userId, userRole, prefs, userCreatedAt);
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => n.isUnread).length);
    } catch (error) {
      logger.error('Error fetching notifications:', error);
    }
  }, [userId, userRole, userCreatedAt]);

  useEffect(() => {
    fetchNotifications();
    
    // Poll for new notifications every 60 seconds
    const interval = setInterval(fetchNotifications, 60000);
    
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, setOpen]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search, setOpen]);

  useEffect(() => {
    return () => {
      onOpenChange?.(false);
    };
  }, [onOpenChange]);

  const handleDismiss = async (notificationId: string) => {
    try {
      const success = await dismissNotification(userId, notificationId);
      if (success) {
        // Update local state
        setNotifications(prev => 
          prev.map(n => n.id === notificationId ? { ...n, isUnread: false } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      logger.error('Error dismissing notification:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    setLoading(true);
    try {
      const success = await markAllAsRead(userId);
      if (success) {
        // Update local state
        setNotifications(prev => prev.map(n => ({ ...n, isUnread: false })));
        setUnreadCount(0);
      }
    } catch (error) {
      logger.error('Error marking all notifications as read:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = (notification: NotificationDisplayItem) => {
    // Dismiss notification
    if (notification.isUnread) {
      handleDismiss(notification.id);
    }
    
    // Navigate if action URL provided - navigate BEFORE closing dropdown
    if (notification.actionUrl && typeof notification.actionUrl === 'string' && notification.actionUrl.trim() !== '') {
      // Navigate first to ensure navigation completes before component unmounts
      navigate(notification.actionUrl);
      // Close dropdown after navigation
      setOpen(false);
    }
  };

  const handleManageClick = () => {
    setOpen(false);
    navigate('/admin/settings?tab=notifications');
  };

  const getPriorityStyles = (priority: string) => {
    switch (priority) {
      case 'high':
        return { border: 'border-l-error-500', bg: 'bg-error-500/5', icon: 'text-error-500' };
      case 'normal':
        return { border: 'border-l-accent-500', bg: 'bg-accent-500/5', icon: 'text-accent-500' };
      case 'low':
        return { border: 'border-l-primary-500/30', bg: 'bg-primary-500/5', icon: 'text-primary-500/60' };
      default:
        return { border: 'border-l-primary-500/30', bg: 'bg-primary-500/5', icon: 'text-primary-500/60' };
    }
  };

  // Only show unread notifications in dropdown
  const unreadNotifications = notifications.filter(n => n.isUnread);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <motion.button
        type="button"
        onClick={() => setOpen(!isOpen)}
        aria-label="Notifications"
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-full border border-primary-500/10 text-primary-500 hover:text-accent-500 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
        initial="rest"
        animate="rest"
        whileHover={iconHover}
        variants={UTILITY_SCALE}
        transition={ICON_MOTION_TRANSITION}
      >
        <motion.span
          className="inline-flex"
          variants={BELL_RING}
          transition={BELL_RING_TRANSITION}
          style={{ originX: 0.5, originY: 0 }}
        >
          <BellIcon className="w-4 h-4" aria-hidden="true" />
        </motion.span>
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-error-500 rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </motion.button>

      {/* Dropdown Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.98, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: -8 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] backdrop-blur rounded-lg shadow-[0_8px_24px_var(--color-primary-alpha-12)] border bg-white border-primary-500/10 z-50"
          >
            {/* Header */}
            <div className="p-4 border-b border-primary-500/10 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-primary-500">
                Notifications
                {unreadCount > 0 && (
                  <span className="ml-2 text-xs font-normal text-primary-500/60">
                    ({unreadCount} unread)
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    disabled={loading}
                    className="text-xs text-accent-500 hover:text-accent-600 font-medium disabled:opacity-50"
                  >
                    Mark all read
                  </button>
                )}
                {/* Manage link for admins */}
                {userRole === 'admin' && (
                  <button
                    onClick={handleManageClick}
                    className="p-1.5 rounded-md text-primary-500/50 hover:text-accent-500 hover:bg-accent-500/10 transition-colors"
                    title="Manage Notifications"
                  >
                    <Cog6ToothIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Notification List */}
            <div className="max-h-[400px] overflow-y-auto">
              {unreadNotifications.length === 0 ? (
                <div className="p-8 text-center">
                  <BellIcon className="w-12 h-12 text-primary-500/20 mx-auto mb-3" />
                  <p className="text-sm text-primary-500/60">No new notifications</p>
                  {userRole === 'admin' && (
                    <button
                      onClick={handleManageClick}
                      className="mt-3 text-xs text-accent-500 hover:text-accent-600 font-medium"
                    >
                      Manage Notifications
                    </button>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-primary-500/5">
                  {unreadNotifications.map((notification) => {
                    const TypeIcon = typeIcons[notification.type] || MegaphoneIcon;
                    const styles = getPriorityStyles(notification.priority);
                    
                    return (
                      <div
                        key={notification.id}
                        className={`p-3 hover:bg-primary-500/5 transition-colors cursor-pointer border-l-4 ${styles.border}`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex items-start gap-3">
                          {/* Type Icon */}
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${styles.bg}`}>
                            <TypeIcon className={`w-4 h-4 ${styles.icon}`} />
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h4 className="text-sm font-semibold text-primary-500 leading-tight">
                                {notification.title}
                              </h4>
                              <span className="text-[10px] text-primary-500/40 whitespace-nowrap flex-shrink-0">
                                {formatRelativeTime(notification.createdAt)}
                              </span>
                            </div>
                            <p className="text-xs text-primary-500/60 mt-1 line-clamp-2 leading-relaxed">
                              {notification.message}
                            </p>
                            <div className="flex items-center justify-between mt-2">
                              {notification.actionText ? (
                                <span className="inline-flex items-center text-xs font-medium text-accent-500 hover:text-accent-600">
                                  {notification.actionText} &rarr;
                                </span>
                              ) : (
                                <span />
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDismiss(notification.id);
                                }}
                                className="inline-flex items-center gap-1 text-[10px] text-primary-500/40 hover:text-accent-500 transition-colors"
                                aria-label="Dismiss"
                              >
                                <CheckIcon className="w-3 h-3" />
                                Dismiss
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer with Manage link for admins */}
            {userRole === 'admin' && unreadNotifications.length > 0 && (
              <div className="p-3 border-t border-primary-500/10">
                <button
                  onClick={handleManageClick}
                  className="w-full text-center text-xs text-accent-500 hover:text-accent-600 font-medium py-1"
                >
                  Manage Notifications
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
